import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { useInviteInstanceMember } from "@frontend/hooks/use-instance"
import { formatInstanceRole } from "@frontend/pages/project-form/lib/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { X } from "lucide-react"
import type { FormEvent } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

const inviteSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  role: z.enum(["admin", "member"])
})

type InviteFormData = z.infer<typeof inviteSchema>

export interface InviteMemberDialogViewProps {
  selectedRole: "admin" | "member"
  emailError?: string
  roleError?: string
  apiError?: string
  isSuccess: boolean
  isPending: boolean
  onClose: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  register: ReturnType<typeof useForm<InviteFormData>>["register"]
  onRoleChange: (role: "admin" | "member") => void
}

export function InviteMemberDialogView({
  selectedRole,
  emailError,
  roleError,
  apiError,
  isSuccess,
  isPending,
  onClose,
  onSubmit,
  register,
  onRoleChange
}: InviteMemberDialogViewProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <Button variant="ghost" size="icon-sm" onClick={onClose} className="absolute right-3 top-3" aria-label="Close">
          <X className="h-4 w-4" />
        </Button>

        <h2 className="text-lg font-semibold mb-4">Invite Member</h2>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              {...register("email")}
              aria-invalid={Boolean(emailError)}
            />
            {emailError ? <p className="text-xs text-destructive">{emailError}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={selectedRole} onValueChange={(value) => onRoleChange(value as "admin" | "member")}>
              <SelectTrigger>
                <SelectValue placeholder="Select role">{formatInstanceRole(selectedRole)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {roleError ? <p className="text-xs text-destructive">{roleError}</p> : null}
          </div>

          {apiError ? <p className="text-xs text-destructive">{apiError}</p> : null}
          {isSuccess ? <p className="text-xs text-green-600">Invitation sent!</p> : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface InviteMemberDialogContainerProps {
  onClose: () => void
}

export function InviteMemberDialogContainer({ onClose }: InviteMemberDialogContainerProps) {
  const inviteMember = useInviteInstanceMember()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role: "member" }
  })

  const selectedRole = watch("role")

  const onSubmit = (data: InviteFormData) => {
    inviteMember.mutate(data, {
      onSuccess: () => {
        onClose()
      }
    })
  }

  return (
    <InviteMemberDialogView
      selectedRole={selectedRole}
      emailError={errors.email?.message}
      roleError={errors.role?.message}
      apiError={inviteMember.isError ? inviteMember.error.message : undefined}
      isSuccess={inviteMember.isSuccess}
      isPending={inviteMember.isPending}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      register={register}
      onRoleChange={(role) => setValue("role", role)}
    />
  )
}

export const InviteMemberDialog = InviteMemberDialogContainer
